import { getConditionalArray } from 'utils/data';
import {
  checkIsAdditionalLoanFlow,
  checkIsHeloanLoanType,
  checkIsHelocLoanType,
  checkIsPurchaseFlow,
  checkIsRefinanceFlow,
} from 'utils/loanApplication';
import { getModifiedFieldChangeKind } from 'utils/pendingChangeKind';

import { ADDITIONAL_LOAN_NAME, CHART_COLORS } from './Chart.constants';
import {
  getAdditionalLoanName,
  getCurrencyValue,
  getLtvName,
  getPercentageValue,
  getTotalLoanAmountName,
} from './Chart.utils';

export const getChartConfig = (
  {
    ltv,
    cltv,
    hltv,
    purchasePrice,
    appraisedValue,
    loanPurpose,
    totalLoanAmount,
    heloc,
    helocLimit,
    modifiedFields,
    modifiedFieldDetails,
    isAssociatedWithFirstLien,
    helocDrawAmount,
  },
  loanType,
) => {
  const pendingFields = { modifiedFields, modifiedFieldDetails };
  const isPurchase = checkIsPurchaseFlow(loanPurpose);
  const isAdditionalLoan = checkIsAdditionalLoanFlow(loanPurpose);
  const isRefinance = checkIsRefinanceFlow(loanPurpose);
  const ltvName = getLtvName(ltv, cltv, hltv);
  const isHeloan = checkIsHeloanLoanType(loanType);
  const isHeloc = checkIsHelocLoanType(loanType);

  const purchaseAppraisedMinValue = Math.min(purchasePrice, appraisedValue);
  const isAppraisedValueLower = appraisedValue === purchaseAppraisedMinValue;
  const isTotalLoanAmountExceeds = totalLoanAmount > purchaseAppraisedMinValue;
  const isAppraisedValueHidden = isAdditionalLoan || (isPurchase && !isAppraisedValueLower);
  const isPurchasePriceHidden = isRefinance || (isPurchase && isAppraisedValueLower);

  const ltvPercentage = isTotalLoanAmountExceeds ? 1 - (ltv - 1) : ltv;
  const exceededLtvPercentage = isTotalLoanAmountExceeds ? 1 - ltvPercentage : 0;
  const cltvPercentage = Math.abs(cltv - ltv);
  const hltvPercentage = Math.abs(cltv - hltv);
  const restPercentage = Math.abs(1 - (ltv + cltvPercentage + hltvPercentage));
  const purchasePricePercentage = isTotalLoanAmountExceeds ? 0 : restPercentage;
  const appraisedValuePercentage = isTotalLoanAmountExceeds ? 0 : restPercentage;

  const isCltvHidden = isTotalLoanAmountExceeds || ltv === cltv;
  const isHltvHidden = isTotalLoanAmountExceeds || hltv === cltv || isHeloan;

  const cltvInfo = [
    {
      name: 'CLTV',
      value: getPercentageValue(cltv),
      changeKind: getModifiedFieldChangeKind(pendingFields, 'Cltv'),
      fieldName: 'Cltv',
    },
    {
      name: isHltvHidden ? ADDITIONAL_LOAN_NAME : 'HELOC Draw',
      value: isHeloc && isAssociatedWithFirstLien ? helocDrawAmount : getCurrencyValue(heloc),
      changeKind: getModifiedFieldChangeKind(pendingFields, 'Heloc'),
      fieldName: 'Heloc',
    },
  ];

  const hltvInfo = [
    {
      name: 'HLTV',
      value: getPercentageValue(hltv),
      changeKind: getModifiedFieldChangeKind(pendingFields, 'Hltv'),
      fieldName: 'Hltv',
    },
    {
      name: getAdditionalLoanName(loanType),
      value: getCurrencyValue(helocLimit),
      changeKind: getModifiedFieldChangeKind(pendingFields, 'HelocLimit'),
      fieldName: 'HelocLimit',
    },
  ];

  const redInfo = [
    {
      name: ltvName,
      value: getPercentageValue(ltv),
      changeKind: getModifiedFieldChangeKind(pendingFields, 'Ltv'),
      fieldName: 'Ltv',
    },
    {
      name: getTotalLoanAmountName(loanType, isAssociatedWithFirstLien),
      value: getCurrencyValue(totalLoanAmount),
      changeKind: getModifiedFieldChangeKind(pendingFields, 'TotalLoanAmount'),
      fieldName: 'TotalLoanAmount',
    },
  ];

  const tooltipConfig = [
    ...getConditionalArray(!isCltvHidden, [
      {
        color: CHART_COLORS.ORANGE,
        info: cltvInfo,
      },
    ]),
    ...getConditionalArray(!isHltvHidden, [
      {
        color: CHART_COLORS.YELLOW,
        info: hltvInfo,
      },
    ]),
  ];

  return [
    {
      color: CHART_COLORS.RED,
      percentage: ltvPercentage,
      info: redInfo,
      isHiddenInfo: isTotalLoanAmountExceeds,
    },
    {
      color: CHART_COLORS.ORANGE,
      percentage: cltvPercentage,
      isHidden: isCltvHidden,
      tooltipConfig,
    },
    {
      color: CHART_COLORS.YELLOW,
      percentage: hltvPercentage,
      isHidden: isHltvHidden,
      tooltipConfig,
    },
    {
      color: CHART_COLORS.GREEN,
      percentage: purchasePricePercentage,
      isHidden: isPurchasePriceHidden,
      info: [
        {
          name: 'Purchase Price',
          value: getCurrencyValue(purchasePrice),
          changeKind: getModifiedFieldChangeKind(pendingFields, 'PurchasePrice'),
          fieldName: 'PurchasePrice',
        },
      ],
    },
    {
      color: CHART_COLORS.BLUE,
      percentage: appraisedValuePercentage,
      isHidden: isAppraisedValueHidden,
      info: [
        {
          name: 'Appraised Value',
          value: getCurrencyValue(appraisedValue),
          changeKind: getModifiedFieldChangeKind(pendingFields, 'AppraisedValue'),
          fieldName: 'AppraisedValue',
        },
      ],
    },
    {
      color: CHART_COLORS.DARK_RED,
      percentage: exceededLtvPercentage,
      isHidden: !isTotalLoanAmountExceeds,
      info: redInfo,
      isHiddenInfo: !isTotalLoanAmountExceeds,
    },
  ];
};
